import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Skeleton,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import CloseIcon from "@mui/icons-material/Close";
import api from "../../api/axios";
import { useSnackbar } from "../../utils/SnackbarProvider";

const VendorUnansweredQuestions = ({ onAnswerSubmitted }) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [answerText, setAnswerText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const { showSnackbar } = useSnackbar();

  // Fetch unanswered questions
  const fetchUnansweredQuestions = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/vendor/dashboard/unanswered-questions");
      setQuestions(data);
    } catch (err) {
      console.error("Failed to fetch unanswered questions", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUnansweredQuestions();
  }, []);

  // Handle answer submission
  const handleAnswerSubmit = async () => {
    if (!answerText.trim() || !selectedQuestion) {
      showSnackbar("Please enter an answer", "warning");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/products/questions/${selectedQuestion._id}/answers`, {
        answerText,
      });
      // Refresh the list
      fetchUnansweredQuestions();
      // Notify parent to refresh count
      if (onAnswerSubmitted) {
        onAnswerSubmitted();
      }
      setOpenDialog(false);
      setAnswerText("");
      setSelectedQuestion(null);
      showSnackbar("Answer posted successfully!", "success");
    } catch (err) {
      console.error("Failed to submit answer", err);
      showSnackbar(
        err.response?.data?.message || "Failed to post answer. Please try again.",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const openAnswerDialog = (question) => {
    setSelectedQuestion(question);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedQuestion(null);
    setAnswerText("");
  };

  // Count of unanswered questions
  const unansweredCount = questions.length;

  return (
    <>
      <Card sx={{ borderRadius: 3, boxShadow: 2, mb: 3, maxWidth: 800, width: '100%' }}>
        <CardContent sx={{ p: 2 }}>
          <Box display="flex" alignItems="center" gap={2} mb={1}>
            <QuestionAnswerIcon sx={{ fontSize: 52, color: "#ff9900" }} />
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {unansweredCount} Unanswered
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Need response
              </Typography>
            </Box>
          </Box>

          {loading ? (
            <Box>
              <Skeleton height={40} />
              <Skeleton height={40} />
              <Skeleton height={40} />
            </Box>
          ) : questions.length === 0 ? (
            <Typography color="text.secondary" align="center" py={3}>
              No unanswered questions. Great job!
            </Typography>
          ) : (
            <List sx={{ maxHeight: 200, overflow: "auto" }}>
              {(showAll ? questions : questions.slice(0, 5)).map((q, idx) => (
                <React.Fragment key={q._id}>
                  <ListItem
                    sx={{
                      py: 0.75,
                      px: 1,
                      "&:hover": { bgcolor: "#f7f7f7", cursor: "pointer" },
                    }}
                    onClick={() => openAnswerDialog(q)}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {q.questionText}
                        </Typography>
                      }
                      secondary={
                        <Box display="flex" alignItems="center" gap={1} mt={0.5} flexWrap="wrap">
                          <Chip
                            label={q.product?.title || "Product"}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.75rem', height: 22 }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {new Date(q.createdAt).toLocaleDateString()}
                          </Typography>
                        </Box>
                      }
                      sx={{ pr: 1 }}
                    />
                    <Button size="small" variant="outlined" sx={{ minWidth: 60, fontSize: '0.75rem' }}>
                      Answer
                    </Button>
                  </ListItem>
                  {idx < (showAll ? questions.length : Math.min(5, questions.length)) - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}

          {questions.length > 5 && (
            <Box mt={2} textAlign="center">
              <Button
                variant="text"
                onClick={() => setShowAll(!showAll)}
              >
                {showAll ? `Show Less` : `View All (${questions.length})`}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Answer Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Answer Question</Typography>
            <IconButton onClick={handleCloseDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedQuestion && (
            <>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Product: {selectedQuestion.product?.title || "Unknown Product"}
              </Typography>
              <Typography variant="body1" paragraph sx={{ bgcolor: "#f5f5f5", p: 2, borderRadius: 1 }}>
                <strong>Q:</strong> {selectedQuestion.questionText}
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="Write your answer here..."
                value={answerText}
                onChange={(e) => setAnswerText(e.target.value)}
                variant="outlined"
                sx={{ mt: 2 }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAnswerSubmit}
            disabled={!answerText.trim() || submitting}
          >
            {submitting ? "Posting..." : "Post Answer"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VendorUnansweredQuestions;